// Agregações do dashboard do perfil, a partir das execuções gravadas no backend (/runs).
// Funções puras: recebem a lista e devolvem números prontos para os cartões e gráficos.

const DAY_MS = 86400 * 1000;

const median = values => {
  const sorted = values.filter(v => Number.isFinite(v)).sort((a, b) => a - b);
  if (!sorted.length) return null;
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};

const mean = values => {
  const numbers = values.filter(v => Number.isFinite(v));
  return numbers.length ? numbers.reduce((a, b) => a + b, 0) / numbers.length : null;
};

// Onde a execução rodou: servidor da API (modo local), um servidor do cluster ou vários nós
export function placement(run) {
  const info = run.run || {};
  if (info.mode !== "slurm") return "Local server";
  return (info.numNodes || 1) > 1 ? "Cluster · parallel (2+ nodes)" : "Cluster · 1 server";
}

export const PLACEMENTS = ["Local server", "Cluster · 1 server", "Cluster · parallel (2+ nodes)"];

// Núcleo·horas: tempo no solver (ou total do job) × processos × threads
export function cpuHours(run) {
  const info = run.run || {};
  const seconds = Number.isFinite(info.elapsedS) ? info.elapsedS : run.durationS;
  if (!Number.isFinite(seconds)) return 0;
  return (seconds * (info.ranks || 1) * (info.mode === "slurm" ? info.threads || 1 : 1)) / 3600;
}

export function summarize(runs) {
  const completed = runs.filter(r => r.status === "completed");
  const durations = completed.map(r => r.durationS);
  return {
    total: runs.length,
    completed: completed.length,
    failed: runs.length - completed.length,
    successRate: runs.length ? completed.length / runs.length : null,
    medianS: median(durations),
    longestS: durations.length ? Math.max(...durations.filter(Number.isFinite)) : null,
    cpuHours: runs.reduce((sum, r) => sum + cpuHours(r), 0),
    totalS: runs.reduce((sum, r) => sum + (Number.isFinite(r.durationS) ? r.durationS : 0), 0),
    parallel: runs.filter(r => placement(r) === "Cluster · parallel (2+ nodes)").length
  };
}

// Uma linha por dia do período (inclusive dias sem execuções), em horário local
export function daily(runs, days, now = Date.now()) {
  const start = new Date(now - (days - 1) * DAY_MS);
  start.setHours(0, 0, 0, 0);
  const rows = [];
  const index = {};
  for (let t = start.getTime(); t <= now; t += DAY_MS) {
    const day = new Date(t);
    index[dayKey(day)] = rows.length;
    rows.push({ date: dayKey(day), completed: 0, failed: 0 });
  }
  runs.forEach(r => {
    const i = index[dayKey(new Date(r.create_date))];
    if (i === undefined) return;
    rows[i][r.status === "completed" ? "completed" : "failed"] += 1;
  });
  return rows;
}

// AAAA-MM-DD no fuso do navegador (o dia em que a pessoa rodou a simulação)
function dayKey(date) {
  const pad = n => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function byPlacement(runs) {
  return PLACEMENTS.map(name => {
    const items = runs.filter(r => placement(r) === name);
    return {
      name,
      runs: items.length,
      medianS: median(items.filter(r => r.status === "completed").map(r => r.durationS)),
      cpuHours: items.reduce((sum, r) => sum + cpuHours(r), 0)
    };
  });
}

export function byKind(runs) {
  return ["2d", "3d"].map(kind => {
    const items = runs.filter(r => r.kind === kind);
    const done = items.filter(r => r.status === "completed");
    return {
      kind,
      runs: items.length,
      failed: items.length - done.length,
      meanS: mean(done.map(r => r.durationS)),
      medianS: median(done.map(r => r.durationS))
    };
  });
}

// Duração legível: 42 s, 3 min 10 s, 1 h 05 min
export function formatDuration(seconds) {
  if (!Number.isFinite(seconds)) return "—";
  if (seconds < 60) return `${seconds < 10 ? seconds.toFixed(1) : Math.round(seconds)} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ${String(Math.round(seconds % 60)).padStart(2, "0")} s`;
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, "0")} min`;
}
